import { useState, useEffect } from 'react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Plus, Calendar, Edit, ChevronLeft, ChevronRight, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitizer';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Notice {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  priority: number;
  is_published: boolean;
}

const NoticePage = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingNotice, setDeletingNotice] = useState<string | null>(null);
  const [expandedNotices, setExpandedNotices] = useState<Set<string>>(new Set());
  
  const noticesPerPage = 5;
  
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminLoading) {
      fetchNotices();
    }
  }, [currentPage, isAdmin, adminLoading]);

  const fetchNotices = async () => {
    try {
      const from = (currentPage - 1) * noticesPerPage;
      const to = from + noticesPerPage - 1;

      // Both admin and public users use the same notices table
      // RLS policies automatically handle access control
      const { count } = await supabase
        .from('notices')
        .select('*', { count: 'exact', head: true });

      const { data, error } = await supabase
        .from('notices')
        .select('id, title, content, created_at, updated_at, priority, is_published')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      setNotices(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching notices:', error);
      toast({
        title: "오류",
        description: "공지사항을 불러오는데 실패했습니다.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isAdmin) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('notices')
        .insert([
          {
            title,
            content,
            priority,
            is_published: true
          }
        ]);

      if (error) throw error;

      toast({
        title: "성공",
        description: "공지사항이 등록되었습니다."
      });

      setTitle('');
      setContent('');
      setPriority(1);
      setDialogOpen(false);
      setCurrentPage(1); // Reset to first page to show new notice
      fetchNotices();
    } catch (error) {
      console.error('Error creating notice:', error);
      toast({
        title: "오류",
        description: "공지사항 등록에 실패했습니다.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    setTitle(notice.title);
    setContent(notice.content);
    setPriority(notice.priority);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isAdmin || !editingNotice) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('notices')
        .update({
          title,
          content,
          priority,
        })
        .eq('id', editingNotice.id);

      if (error) throw error;

      toast({
        title: "성공",
        description: "공지사항이 수정되었습니다."
      });

      setTitle('');
      setContent('');
      setPriority(1);
      setEditingNotice(null);
      setEditDialogOpen(false);
      fetchNotices();
    } catch (error) {
      console.error('Error updating notice:', error);
      toast({
        title: "오류",
        description: "공지사항 수정에 실패했습니다.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noticeId: string) => {
    if (!user || !isAdmin) return;

    setDeletingNotice(noticeId);
    try {
      const { error } = await supabase
        .from('notices')
        .delete()
        .eq('id', noticeId);

      if (error) throw error;

      toast({
        title: "성공",
        description: "공지사항이 삭제되었습니다."
      });

      fetchNotices();
    } catch (error) {
      console.error('Error deleting notice:', error);
      toast({
        title: "오류",
        description: "공지사항 삭제에 실패했습니다.",
        variant: "destructive"
      });
    } finally {
      setDeletingNotice(null);
    }
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingNotice(null);
    setTitle('');
    setContent('');
    setPriority(1);
  };

  const getPriorityBadge = (priority: number) => {
    if (priority >= 3) return <Badge variant="destructive">긴급</Badge>;
    if (priority === 2) return <Badge variant="default">중요</Badge>;
    return <Badge variant="secondary">일반</Badge>;
  };

  const toggleExpanded = (noticeId: string) => {
    setExpandedNotices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noticeId)) {
        newSet.delete(noticeId);
      } else {
        newSet.add(noticeId);
      }
      return newSet;
    });
  };

  const getTruncatedContent = (content: string, isExpanded: boolean) => {
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    if (isExpanded || plainText.length <= 100) {
      return content;
    }
    const truncated = plainText.substring(0, 100) + '...';
    return `<p>${truncated}</p>`;
  };

  if (loading || adminLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">로딩 중...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="공지사항 - Central TG | JAX 모델 소식 및 업데이트"
        description="Central TG의 최신 소식, 제품 업데이트, 연구 지원 정보를 확인하세요. JAX transgenic 모델 관련 중요 공지사항을 제공합니다."
        keywords="Central TG 공지, JAX 모델 소식, transgenic 마우스 업데이트, 실험동물 공지사항, 연구지원 소식, 신제품 안내"
        canonical="https://centraltg.co.kr/notice"
      />
      <Header />
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2 mt-4 sm:mt-6">공지사항</h1>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Central TG Inc. 센트럴티지<span style={{ fontSize: "70%" }} className="align-baseline">(주)</span>의 최신 소식과 중요한 공지사항을 확인하세요.
              </p>
            </div>
            
            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full sm:w-auto text-sm">
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden xs:inline">공지 작성</span>
                    <span className="xs:hidden">작성</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>새 공지사항 작성</DialogTitle>
                    <DialogDescription>
                      새로운 공지사항을 작성하여 사용자들에게 알림을 전달하세요.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="title">제목</Label>
                      <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="공지사항 제목을 입력하세요"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="priority">우선순위</Label>
                      <select
                        id="priority"
                        value={priority}
                        onChange={(e) => setPriority(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-input rounded-md"
                      >
                        <option value={1}>일반</option>
                        <option value={2}>중요</option>
                        <option value={3}>긴급</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="content">내용</Label>
                      <RichTextEditor
                        content={content}
                        onChange={setContent}
                        placeholder="공지사항 내용을 입력하세요"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setDialogOpen(false)}
                      >
                        취소
                      </Button>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? "등록 중..." : "등록"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="space-y-2 sm:space-y-3">
            {notices.length === 0 ? (
              <Card>
                <CardContent className="text-center py-6 sm:py-8">
                  <div className="text-muted-foreground">
                    <Calendar className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3 opacity-50" />
                    <p className="text-sm sm:text-base">등록된 공지사항이 없습니다.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              notices.map((notice) => (
                <Card key={notice.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3 sm:pb-4 px-4 sm:px-6 pt-4 sm:pt-6">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          {getPriorityBadge(notice.priority)}
                        </div>
                        <CardTitle className="text-base sm:text-lg leading-tight pr-2">{notice.title}</CardTitle>
                        <CardDescription className="flex items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                            <span className="truncate">
                              {format(new Date(notice.created_at), 'PPP', { locale: ko })}
                            </span>
                          </span>
                        </CardDescription>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 sm:gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(notice)}
                            aria-label={`공지사항 수정: ${notice.title}`}
                            className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive h-8 w-8 p-0 sm:h-9 sm:w-9"
                                aria-label={`공지사항 삭제: ${notice.title}`}
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>공지사항 삭제</AlertDialogTitle>
                                <AlertDialogDescription>
                                  정말로 이 공지사항을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>취소</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(notice.id)}
                                  disabled={deletingNotice === notice.id}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {deletingNotice === notice.id ? "삭제 중..." : "삭제"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                   <CardContent className="pt-0 px-4 sm:px-6 pb-4 sm:pb-6">
                     <div 
                       className="prose prose-sm max-w-none text-foreground [&_table]:border-collapse [&_table]:border [&_table]:border-border [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:p-3 [&_th]:border [&_th]:border-border [&_th]:p-3 [&_th]:bg-muted/50 [&_th]:font-semibold [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80 [&_a]:font-medium [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground text-sm sm:text-base [&_h1]:text-xl [&_h1]:sm:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:sm:mb-4 [&_h1]:mt-4 [&_h1]:sm:mt-6 [&_h1]:leading-tight [&_h2]:text-lg [&_h2]:sm:text-xl [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:sm:mb-3 [&_h2]:mt-3 [&_h2]:sm:mt-5 [&_h2]:leading-tight [&_h3]:text-base [&_h3]:sm:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:sm:mt-4 [&_h3]:leading-tight [&_p]:mb-2 [&_p]:sm:mb-3 [&_p]:leading-relaxed"
                       dangerouslySetInnerHTML={{ 
                         __html: sanitizeHtml(getTruncatedContent(notice.content, expandedNotices.has(notice.id))) 
                       }}
                     />
                     {notice.content.replace(/<[^>]*>/g, '').trim().length > 100 && (
                       <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(notice.id)}
                          className="mt-2 text-primary hover:text-primary hover:bg-transparent text-xs sm:text-sm h-8"
                        >
                         {expandedNotices.has(notice.id) ? (
                           <>
                             <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                             접기
                           </>
                         ) : (
                           <>
                             <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                             더보기
                           </>
                         )}
                       </Button>
                     )}
                   </CardContent>
                </Card>
              ))
            )}
          </div>
          
          {/* Pagination Controls */}
          {totalCount > noticesPerPage && (
            <div className="flex flex-col xs:flex-row justify-center items-center gap-2 xs:gap-4 mt-6 sm:mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-full xs:w-auto text-xs sm:text-sm"
              >
                <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                이전
              </Button>
              
              <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto py-2">
                {Array.from({ length: Math.ceil(totalCount / noticesPerPage) }).map((_, index) => {
                  const pageNumber = index + 1;
                  const isCurrentPage = pageNumber === currentPage;
                  
                  return (
                    <Button
                      key={pageNumber}
                      variant={isCurrentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNumber)}
                      className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm shrink-0"
                    >
                      {pageNumber}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalCount / noticesPerPage)))}
                disabled={currentPage === Math.ceil(totalCount / noticesPerPage)}
                className="w-full xs:w-auto text-xs sm:text-sm"
              >
                다음
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
            </div>
          )}
          
          {/* Edit Dialog */}
          {isAdmin && (
            <Dialog open={editDialogOpen} onOpenChange={closeEditDialog}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>공지사항 수정</DialogTitle>
                  <DialogDescription>
                    기존 공지사항을 수정합니다.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="edit-title">제목</Label>
                    <Input
                      id="edit-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="공지사항 제목을 입력하세요"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-priority">우선순위</Label>
                    <select
                      id="edit-priority"
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-input rounded-md"
                    >
                      <option value={1}>일반</option>
                      <option value={2}>중요</option>
                      <option value={3}>긴급</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="edit-content">내용</Label>
                    <ScrollArea className="h-[400px] w-full border rounded-md [&>[data-radix-scroll-area-viewport]]:!block">
                      <div className="p-1">
                        <RichTextEditor
                          content={content}
                          onChange={setContent}
                          placeholder="공지사항 내용을 입력하세요"
                        />
                      </div>
                    </ScrollArea>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={closeEditDialog}
                    >
                      취소
                    </Button>
                    <Button type="submit" disabled={submitting}>
                      {submitting ? "수정 중..." : "수정"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NoticePage;
